import React from "react";
import { FieldBadge } from "../field-badge";
import { Avatar } from "../../../profile_utils";
import { MultiSelectComponent } from "../../../../ui/input_multi_select/controller";
import { AddressAutocomplete } from "../address-autocomplete";

interface Profile {
  id: number;
  name: string;
  avatar_url?: string;
  project_address?: string;
  project_address_id?: number;
  project_sectors?: string[];
  project_sector_ids?: number[];
  project_contribution?: number;
}

interface CandidatMainProfileProps {
  profile: Profile;
  secteurs?: any[];
}

export function CandidatMainProfile({
  profile,
  secteurs = [],
}: CandidatMainProfileProps) {
  const formatCurrency = (value?: number) => {
    if (!value || value === 0) return "non renseigné";
    return new Intl.NumberFormat("fr-FR", {
      style: "currency",
      currency: "EUR",
    }).format(value);
  };

  const formatSectors = (sectors?: string[]) => {
    if (!sectors || sectors.length === 0) return "non renseigné";
    if (sectors.length === 1) return sectors[0];
    return `${sectors[0]} (+${sectors.length - 1})`;
  };

  return (
    <div className="h-full flex flex-col">
      <div className="px-4 py-2 border-b border-slate-400/60 h-[51px] items-center flex">
        <h3 className="font-semibold text-lg text-slate-800">Votre profil</h3>
      </div>

      <div className="p-6 space-y-6">
        {/* Logo and Name */}
        <div className="flex items-center gap-3">
          <Avatar
            entityType="candidat"
            imageUrl={profile.avatar_url}
            name={profile.name}
            size="xl"
          />
          <div className="font-semibold text-slate-900">{profile.name}</div>
        </div>

        {/* Fields */}
        <div className="space-y-3 text-sm">
          {/* Géolocalisation */}
          <div className="flex items-start justify-between gap-4">
            <span className="font-medium text-slate-700 min-w-[100px]">
              Géolocalisation :
            </span>
            <div className="flex-1">
              <FieldBadge
                profileId={profile.id}
                profileType="candidat"
                fieldName="project_address_id"
                label={profile.project_address || "non renseigné"}
                value={profile.project_address}
              >
                <div className="pt-4">
                  <label className="block text-sm font-medium text-slate-700 mb-2">
                    Localisation recherchée
                  </label>
                  <AddressAutocomplete
                    name="project_address_id"
                    value={profile.project_address_id}
                    displayValue={profile.project_address}
                    endpoint="/addresses"
                    placeholder="Rechercher une adresse..."
                  />
                </div>
              </FieldBadge>
            </div>
          </div>

          {/* Secteur(s) */}
          <div className="flex items-start justify-between gap-4">
            <span className="font-medium text-slate-700 min-w-[100px]">
              Secteur(s) :
            </span>
            <div className="flex-1">
              <FieldBadge
                profileId={profile.id}
                profileType="candidat"
                fieldName="project_sector_ids"
                label={formatSectors(profile.project_sectors)}
                value={profile.project_sectors}
              >
                <div className="pt-4">
                  <label className="block text-sm font-medium text-slate-700 mb-2">
                    Secteurs d'activité recherchés
                  </label>
                  <MultiSelectComponent
                    name="project_sector_ids"
                    selectedIds={profile.project_sector_ids || []}
                    options={secteurs}
                    isMulti={true}
                    creatable={false}
                  />
                </div>
              </FieldBadge>
            </div>
          </div>

          {/* Apport */}
          <div className="flex items-start justify-between gap-4">
            <span className="font-medium text-slate-700 min-w-[100px]">
              Apport :
            </span>
            <div className="flex-1">
              <FieldBadge
                profileId={profile.id}
                profileType="candidat"
                fieldName="project_contribution"
                label={formatCurrency(profile.project_contribution)}
                value={profile.project_contribution}
              >
                <div className="pt-4">
                  <label className="block text-sm font-medium text-slate-700 mb-2">
                    Apport maximum (en €)
                  </label>
                  <input
                    type="number"
                    name="project_contribution"
                    defaultValue={profile.project_contribution}
                    className="w-full px-3 py-2 border border-slate-300 rounded-lg focus:ring-2 focus:ring-default-600"
                  />
                </div>
              </FieldBadge>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}
